import React, { useState, useEffect, useMemo } from 'react';
import * as RootNavigation from '../utils/navigationRef';
import {
  View,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Button, { BUTTON_TYPES } from '../components/Button';
import PickerSelect from '../components/PickerSelect';
import BillingInfoLabel from '../components/BillingInfoLabel';
import { Formik } from 'formik';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { getReprice, getPurchase } from '../store/actions/payment';
import { frontierFlightReimbursmentUpdate, updateReportingFields } from '../store/actions/search';
import PurchaseCreditCardFormik from '../components/PurchaseCreditCardFormik';
import CustomFieldsFormik from '../components/CustomFieldsFormik';
import FixedNextButtonBar from '../components/shared/FixedNextButtonBar';
import CheckoutStepper from '../components/CheckoutStepper';
import { Screens } from '../constants/ScreenNames';
import * as Yup from 'yup';
import RowView from '../components/RowView';
import Icon from '../components/Icon';
import {
  selectAirSegments,
  selectTravelCartCreditCards,
  selectCheckoutStepperSteps,
  selectIsReasonCodeDropdownAllowed,
} from '../store/selectors/trip.selector';
import { selectSeatSelection } from '../store/selectors/seat.selector';
import {
  selectPolicyException,
  selectTripSummary,
  selectAirRepriceError,
  selectTripResults,
  selectSearchInput,
} from '../store/selectors/search.selector';
import { selectTotalCartAmount } from '../store/selectors/cart.selector';
import Modal from '../components/Modal';
import { getCountries, hasRegisteredStates } from '../utils/country.utils';
import { CommonProperties, CommonStyles, Color } from '../styles';
import TotalPrice from '../components/TotalPrice';
import BaseText from '../components/BaseText';
import { Checkbox } from '../components/Checkbox';
import MessageAlert from '../components/MessageAlert';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import {
  selectedCarComponentForPurchase,
  selectedCarComponents,
} from '../store/selectors/car.selector';
import { AA_ENABLED, REBOOK_ENABLED, EVENTS_ENABLED } from '../config/env';
import FrontierFlightReimbursmentForm from '../components/FrontierFlightReimbursmentForm';
import BxAuth from '../components/AmericanAirlines/BxAuth';
import {
  getCreditCardVendor,
  sponsoredTravelCreditCardsData,
  showVirtualPaymentMessageForMsite,
} from '../utils/creditcard.util';
import Messages from '../components/event/Messages';
import { pageKeys } from '../constants/PageKeys';
import { selectTezEvent, selectTezEventGroup } from '../store/selectors/events.selector';
import { selectTokenData } from '../store/selectors/auth.selector';
import {
  getCustomFieldValidation,
  getReportingFields,
  getDeploymentMap,
} from '../utils/customFieldUtils';
import { PurchaseScreenConstants } from '../constants/defaultData';
import { AirLegs, Source, searchTypesEnum } from '../constants/Enums';
import { buildAirTravelComponentResponse } from '../utils/air.util';
import { selectAirComponents } from '../store/selectors/air.selector';
import ModalDropdown from '../components/ModalDropdown';
import { COMPONENT_TYPES } from '../constants/Enums';
const { NULL_UDID, REASON_CODE } = PurchaseScreenConstants;
const { FIRST_LEG, SECOND_LEG } = AirLegs;
const { AIR } = searchTypesEnum;
const US = 'US';
const US_ZipCodeDigitsQuantity = 5;

const creditCardFormDetails = {
  ccPreCreditCard: '',
  ccName: '',
  ccProvider: '',
  ccType: '',
  ccNumber: '',
  ccMonth: '',
  ccYear: '',
  ccCVV: '',
  ccFirstName: '',
  ccBillingAddress: '',
  ccCity: '',
  ccCountry: '',
  ccState: '',
  ccZipCode: '',
  cardHolderEmail: '',
};
const frontierAddressDetails = {
  ccBillingAddressFrontier: '',
  ccCityFrontier: '',
  ccCountryFrontier: '',
  ccStateFrontier: '',
  ccZipCodeFrontier: '',
};
//air is an array because we need to save values of first and second leg
const policyReasonCodes = {
  air: ['', ''],
  car: '',
  hotel: '',
};

const placeholderOption = { label: REASON_CODE, value: REASON_CODE };

const PolicyReasonsDropdown = (props) => {
  const {
    redLetters,
    policyText,
    description,
    label,
    onChange,
    items,
    value,
    isReasonCodeDropdownAllowed,
  } = props;
  return (
    <View>
      <BaseText style={styles.policyText}>
        {redLetters} <BaseText style={styles.policyException}>{policyText}</BaseText>
      </BaseText>
      {description ? (
        <BaseText style={styles.policyReason}>
          <BaseText style={styles.policyException}>{description}</BaseText>
        </BaseText>
      ) : (
        <></>
      )}
      {/* Placeholder removed because a default value is added on list items */}
      {isReasonCodeDropdownAllowed ? (
        <PickerSelect label={label} onValueChange={onChange} items={items} value={value} />
      ) : (
        ''
      )}
    </View>
  );
};

const setNewCreditCardFlag = (isType, isSponsoredTravel, validKey, newCardValue) => {
  return isType && !isSponsoredTravel && validKey === newCardValue;
};

const selectedHotelUDIDOverNightStay = NULL_UDID;
const airnewcardValue = 'airnewcard';
const airextranewcardValue = 'airextranewcard';
const hotelnewcardValue = 'hotelnewcard';
const carnewcardValue = 'carnewcard';
const railnewcardValue = 'railnewcard';
const EMPTY_CREDIT_CARD_KEY = '';

const PurchaseScreen = ({ navigation }) => {
  const { t } = useTranslation(i18nNS.checkout);
  const { t: t2 } = useTranslation(i18nNS.myTrips);
  const { tKey } = useComponentTKey('PurchaseScreen');
  const dispatch = useDispatch();
  const paymentInProgress = useSelector((state) => state.payment.paymentInProgress);
  const [countryList, setCountryList] = useState([]);
  const cartTotal = useSelector(selectTotalCartAmount);
  const travelCart = useSelector((state) => state.search && state.search.travelCart);
  const travelCartCreditCards = useSelector(selectTravelCartCreditCards);
  const creditCardProviders = formatProvidersList(travelCart.CreditCardProviders);
  const hotelInclusion = travelCart?.HotelInclusion;
  const policyException = useSelector(selectPolicyException);
  const airRepriceError = useSelector(selectAirRepriceError);
  const tripSummary = useSelector(selectTripSummary);
  const seatsSelected = useSelector(selectSeatSelection);
  const airSegments = useSelector(selectAirSegments);
  const checkoutSteps = useSelector(selectCheckoutStepperSteps);
  const selectedCar = useSelector(selectedCarComponentForPurchase);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);
  // converting CountryDetails to state so values are persistent
  // with this change CountryDetails can be used to validate with Yup correctly
  const [CountryDetails, setCountryDetails] = useState({ ccCountry: '', ccCountryFrontier: '' });

  const hotelUDIDOvernightArray = hotelInclusion?.LstHotelUDIDOverNightStays?.map((item) => {
    const { udid, description } = item;
    return { value: udid?.toString(), label: description };
  });

  const lstHotelUDIDOverNightStays = hotelUDIDOvernightArray
    ? [placeholderOption, ...hotelUDIDOvernightArray]
    : [];

  const reportingFields = getReportingFields(travelCart?.ReportingFields, false);
  // deploymentMap is going to store the UDIDs displayed on this screen
  const deploymentMap = getDeploymentMap(reportingFields);

  const customFields = reportingFields.reduce(
    (object, field) => ({ ...object, [field.UDIDText]: '' }),
    {}
  );

  const handleCountryChange = (country) => {
    //Saving the country value in this object in order to verify if it has states
    setCountryDetails({ ...CountryDetails, ccCountry: country });
  };

  const handleCountryFrontierChange = (country) => {
    //Saving the country value in this object in order to verify if it has states
    setCountryDetails({ ...CountryDetails, ccCountryFrontier: country });
  };

  //here policy reason codes are saved
  const objPolicyExceptions = useMemo(
    () =>
      policyException.map((policy) => {
        const { ComponentName, PolicyException, LegNumber } = policy;
        const obj = {
          type: ComponentName,
          text: PolicyException,
          legNumber: LegNumber ? LegNumber : null,
          reasons:
            PolicyException?.toLowerCase() !== 'none' && PolicyException !== ''
              ? travelCart[ComponentName?.capitalize() + 'PolicyReasons']?.map((reason) => {
                  return { label: reason?.PolicyDescription, value: reason?.Policycode };
                })
              : null,
        };
        let reasons = null;
        if (obj?.reasons) {
          reasons = obj.reasons[0];
          //added default option in front of array
          obj.reasons.splice(0, 0, placeholderOption);
        }
        return { ...obj, value: reasons };
      }),
    [policyException, travelCart]
  );

  function formatProvidersList(ccProvdiers) {
    return ccProvdiers?.map((ccProvider) => {
      return { label: ccProvider?.CreditCardVendor, value: ccProvider?.CreditCardVendorCode };
    });
  }

  const isCardExpired = (card) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // JavaScript months are zero-based
    return (
      card.CreditCardExpiryYear < currentYear ||
      (card.CreditCardExpiryYear === currentYear && card.CreditCardExpiryMonth < currentMonth)
    );
  };

  const getFirstValidCCKey = (cards, newCardValue, isType) => {
    if (!isType) {
      // Empty string will be used for unselected components (Air,Hotel,Car,Rail)
      return EMPTY_CREDIT_CARD_KEY;
    }
    const firstValidCC = cards.find((item) => !item.disabled);
    if (firstValidCC) {
      return firstValidCC.value;
    }
    return newCardValue;
  };

  const getCreditCardDetails = (cards, filterCondition) => {
    return [
      ...cards
        .filter(filterCondition)
        .map((card) => {
          return {
            label: card.CreditCardDescription,
            value: card.CreditCardKey,
            disabled: isCardExpired(card),
          };
        })
        .sort((a, b) => a.disabled - b.disabled),
    ];
  };

  const [airCCDetails] = useState(
    getCreditCardDetails(travelCartCreditCards, (card) => card.isAir)
  );

  const [airExtraCCDetails] = useState(
    getCreditCardDetails(travelCartCreditCards, (card) => card.isAirExtra)
  );

  const [hotelCCDetails] = useState(
    getCreditCardDetails(travelCartCreditCards, (card) => card.isHotel)
  );

  const [carCCDetails] = useState(
    getCreditCardDetails(travelCartCreditCards, (card) => card.isCar)
  );

  const [railCCDetails] = useState(
    getCreditCardDetails(travelCartCreditCards, (card) => card.isRail)
  );

  const isAir = travelCart.IsAir;
  const isHotel = travelCart.IsHotel;
  const isCar = travelCart.IsCar;
  const isRail = travelCart.IsRail;

  let isAirExtra = false;
  Object.keys(seatsSelected).forEach(function (segmentId) {
    if (
      seatsSelected[segmentId] !== undefined &&
      Object.keys(seatsSelected[segmentId]).length > 0
    ) {
      const segment = airSegments.find((seg) => seg.SegmentId === segmentId);
      if (EVENTS_ENABLED) {
        Object.keys(seatsSelected[segmentId]).forEach(function (passengerName) {
          const passengerData = seatsSelected[segmentId][passengerName];
          if (passengerData?.Price !== 0) {
            isAirExtra = true;
            return;
          }
        });
      } else {
        if (segment !== undefined && seatsSelected[segmentId][0]?.Price != '0') {
          isAirExtra = true;
          return;
        } else {
          if (!isAirExtra) {
            isAirExtra = false;
          }
        }
      }
    }
  });

  const isAirSponsoredTravel = travelCart?.IsAirCreditCardMandatory;
  const isAirExtraSponsoredTravel = travelCart?.IsAirExtraCreditCardMandatory;
  const isCarSponsoredTravel = travelCart?.IsCarCreditCardMandatory;
  const isHotelSponsoredTravel = travelCart?.IsHotelCreditCardMandatory;
  const isRailSponsoredTravel = travelCart?.IsRailCreditCardMandatory;
  const firstAirCCValidKey = getFirstValidCCKey(airCCDetails, airnewcardValue, isAir);
  const firstAirExtraCCValidKey = getFirstValidCCKey(
    airExtraCCDetails,
    airextranewcardValue,
    isAirExtra
  );
  const firstHotelCCValidKey = getFirstValidCCKey(hotelCCDetails, hotelnewcardValue, isHotel);
  const firstCarCCValidKey = getFirstValidCCKey(carCCDetails, carnewcardValue, isCar);
  const firstRailCCValidKey = getFirstValidCCKey(railCCDetails, railnewcardValue, isRail);
  const [repriceValid, setRepriceValid] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [airCreditCard, setAirCreditCard] = useState(
    setNewCreditCardFlag(isAir, isAirSponsoredTravel, firstAirCCValidKey, airnewcardValue)
  );
  const [airExtraCreditCard, setAirExtraCreditCard] = useState(
    setNewCreditCardFlag(
      isAirExtra,
      isAirExtraSponsoredTravel,
      firstAirExtraCCValidKey,
      airextranewcardValue
    )
  );
  const [hotelCreditCard, setHotelCreditCard] = useState(
    setNewCreditCardFlag(isHotel, isHotelSponsoredTravel, firstHotelCCValidKey, hotelnewcardValue)
  );
  const [carCreditCard, setCarCreditCard] = useState(
    setNewCreditCardFlag(isCar, isCarSponsoredTravel, firstCarCCValidKey, carnewcardValue)
  );
  const [railCreditCard, setRailCreditCard] = useState(
    setNewCreditCardFlag(isRail, isRailSponsoredTravel, firstRailCCValidKey, railnewcardValue)
  );
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [checkbox, setIsCheckbox] = useState(false);
  const selectedAirComponents = useSelector(selectAirComponents);
  const selectInput = useSelector(selectSearchInput);
  const isRedisCacheKeyPresent =
    useSelector(selectTripResults)?.RequestedComponents[0]?.RedisCacheKey?.length > 0;
  const isReasonCodeDropdownAllowed = useSelector(selectIsReasonCodeDropdownAllowed);
  __DEV__ && console.log('isReasonCodeDropdownAllowed', isReasonCodeDropdownAllowed);
  useEffect(() => {
    async function fetchrePrice() {
      setPaymentLoading(true);
      const selectedAir = isRedisCacheKeyPresent
        ? await buildAirTravelComponentResponse(selectedAirComponents, selectInput)
        : null;
      const bReprice = await dispatch(getReprice(selectedAir, travelCart?.Passengers));
      setRepriceValid(bReprice);
      (async () => {
        const res = await getCountries();

        setCountryList(res);
      })();
      setPaymentLoading(false);
      if (REBOOK_ENABLED) {
        setAirCreditCard(true);
      }
    }
    fetchrePrice();
  }, [dispatch]);

  const getUS_ZipCodeValidation = () => {
    return Yup.string()
      .required(t(tKey('Required')))
      .length(US_ZipCodeDigitsQuantity, `Zip Code Must Contain ${US_ZipCodeDigitsQuantity} Digits`);
  };

  const creditCardSchema = Yup.lazy((values) => {
    let cvvValidation;
    if (values.rail.ccPreCreditCard === railnewcardValue) {
      cvvValidation = Yup.string().required(t(tKey('CVVRequired')));
    }
    const valid = Yup.object().shape({
      ccName: Yup.string()
        .min(2, t(tKey('TooShort')))
        .max(50, t(tKey('TooLong')))
        .required(t(tKey('Required'))),
      ccNumber: Yup.string()
        .required(t(tKey('Required')))
        .test('ccVendor', t(tKey('InvalidCard')), (val) => {
          return getCreditCardVendor(val) != null;
        }),
      ccProvider: Yup.string()
        .required(t(tKey('Required')))
        .test('ccProvider', t(tKey('SelectCardProvider')), (val) => {
          return val != t(tKey('CardProvider'));
        }),
      ccType: !EVENTS_ENABLED
        ? Yup.string()
            .required(t(tKey('Required')))
            .test('ccType', t(tKey('SelectCardType')), (val) => {
              return val != t(tKey('CardType'));
            })
        : null,
      ccMonth: Yup.string()
        .required(t(tKey('Required')))
        .test('ccMonth', t(tKey('SelectMonth')), (val) => {
          return val != t(tKey('SelectMonth'));
        }),
      ccYear: Yup.string()
        .required(t(tKey('Required')))
        .test('ccYear', t(tKey('SelectYear')), (val) => {
          return val != t(tKey('SelectYear'));
        }),
      ccCVV: cvvValidation,
      ccFirstName: Yup.string().required(t(tKey('Required'))),
      ccBillingAddress: Yup.string().required(t(tKey('Required'))),
      ccCity: Yup.string().required(t(tKey('Required'))),
      ccCountry: Yup.string().required(t(tKey('Required'))),
      ccState: hasRegisteredStates(CountryDetails.ccCountry)
        ? Yup.string().required(t(tKey('Required')))
        : null,
      ccZipCode:
        CountryDetails.ccCountry === US
          ? getUS_ZipCodeValidation()
          : Yup.string().required(t(tKey('Required'))),
      ccBillingAddressFrontier: REBOOK_ENABLED
        ? checkbox
          ? null
          : Yup.string().required(t(tKey('Required')))
        : null,
      ccCityFrontier: REBOOK_ENABLED
        ? checkbox
          ? null
          : Yup.string().required(t(tKey('Required')))
        : null,
      ccCountryFrontier: REBOOK_ENABLED
        ? checkbox
          ? null
          : Yup.string().required(t(tKey('Required')))
        : null,
      ccStateFrontier: REBOOK_ENABLED
        ? checkbox
          ? null
          : hasRegisteredStates(CountryDetails.ccCountryFrontier)
          ? Yup.string().required(t(tKey('Required')))
          : null
        : null,
      ccZipCodeFrontier: REBOOK_ENABLED
        ? checkbox
          ? null
          : CountryDetails.ccCountryFrontier === US
          ? getUS_ZipCodeValidation()
          : Yup.string().required(t(tKey('Required')))
        : null,
      cardHolderEmail: Yup.string()
        .email(t(tKey('InvalidCardHolderEmail')))
        .required(t(tKey('Required')))
        .nullable()
        .trim(),
    });

    const validObj = {};
    if (airCreditCard) {
      validObj.air = valid;
    }

    if (airExtraCreditCard) {
      validObj.airExtra = valid;
    }

    if (hotelCreditCard) {
      validObj.hotel = valid;
    }

    if (carCreditCard) {
      validObj.car = valid;
    }

    if (railCreditCard) {
      validObj.rail = valid;
    }

    validObj.customFields = Yup.object().shape(
      getCustomFieldValidation(reportingFields, Yup, deploymentMap)
    );

    return Yup.object().shape(validObj);
  });

  const checkFixedButtonDisable = (repriceValid, formikProps, termsAgreed) => {
    if (!repriceValid) {
      return true;
    }
    if (!termsAgreed || !formikProps.isValid) {
      return true;
    } else {
      if (isAir) {
        if (
          formikProps.values['air'].ccPreCreditCard == '' ||
          formikProps.values['air'].ccPreCreditCard == 'Select a Card'
        ) {
          if (REBOOK_ENABLED) {
            return false;
          }
          return true;
        }
        if (isReasonCodeDropdownAllowed) {
          if (
            //this condition checks if air is OOP and both drop downs are selected
            (getComponentPolicy('air', FIRST_LEG)?.reasons &&
              (formikProps.values.policy.air[0] === '' ||
                formikProps.values.policy.air[0] === REASON_CODE)) ||
            (getComponentPolicy('air', SECOND_LEG)?.reasons &&
              (formikProps.values.policy.air[1] === '' ||
                formikProps.values.policy.air[1] === REASON_CODE) &&
              isReasonCodeDropdownAllowed)
          ) {
            return true;
          }
        }
      }
      if (isAirExtra) {
        if (
          formikProps.values['airExtra'].ccPreCreditCard == '' ||
          formikProps.values['airExtra'].ccPreCreditCard == 'Select a Card'
        ) {
          return true;
        }
      }
      if (isHotel) {
        if (
          formikProps.values['hotel'].ccPreCreditCard == '' ||
          formikProps.values['hotel'].ccPreCreditCard == 'Select a Card'
        ) {
          return true;
        }
        if (
          //this condition checks if hotel is OOP and drop down is selected
          getComponentPolicy('hotel')?.reasons &&
          isReasonCodeDropdownAllowed &&
          (formikProps.values.policy.hotel === '' ||
            formikProps.values.policy.hotel === REASON_CODE)
        ) {
          return true;
        }
      }
      if (isCar) {
        if (
          formikProps.values['car'].ccPreCreditCard == '' ||
          formikProps.values['car'].ccPreCreditCard == 'Select a Card'
        ) {
          return true;
        }
        if (
          //this condition checks if car is OOP and drop down is selected
          getComponentPolicy('car')?.reasons &&
          isReasonCodeDropdownAllowed &&
          (formikProps.values.policy.car === '' || formikProps.values.policy.car === REASON_CODE)
        ) {
          return true;
        }
      }
      if (isRail) {
        if (
          formikProps.values['rail'].ccPreCreditCard == '' ||
          formikProps.values['rail'].ccPreCreditCard == 'Select a Card'
        ) {
          return true;
        }
      }
      if (
        hotelInclusion?.IsHotelMissingInSegment &&
        isReasonCodeDropdownAllowed &&
        (formikProps.values.selectedHotelUDIDOverNightStay === NULL_UDID ||
          formikProps.values.selectedHotelUDIDOverNightStay === REASON_CODE)
      ) {
        return true;
      }
      return false;
    }
  };
  const isCheckboxChecked = (isTrue) => {
    if (isTrue) {
      setIsCheckbox(true);
    } else {
      setIsCheckbox(false);
    }
  };
  const getComponentPolicy = (type, legNumber = undefined) => {
    return legNumber
      ? objPolicyExceptions.find((obj) => obj.type === type && obj.legNumber === legNumber)
      : objPolicyExceptions.find((obj) => obj.type === type);
  };
  const getFormikValue = (component, legNumber) => {
    //if air we need to select the policy for the proper leg number - 1
    //as policy codes start in 0
    if (component === AIR) {
      return `policy.${component}[${legNumber - 1}]`;
    }
    return `policy.${component}`;
  };
  const getComponentDescription = (component, legNumber) => {
    let date = '';
    let service = '';
    let city = '';
    const dateFormat = 'ddd, MMM Do, YYYY hh:mm A';
    if (component === 'air') {
      const airSegments = tripSummary.AirResponses[0].AirLegs[legNumber - 1].AirSegments;
      const { DepartureDate, DepartTime } = airSegments[0];
      const { ArrivalAirportCityName } = airSegments[airSegments.length - 1];
      //Added on-air conversion format because its dates have the following format:
      //2023-07-03 09:45 AM/PM
      date = moment(`${DepartureDate} ${DepartTime}`, 'YYYY-MM-DD hh:mm A').format(dateFormat);
      service = 'flight to';
      city = ArrivalAirportCityName;
    }
    if (component === 'car') {
      const carDetail = JSON.parse(tripSummary?.CarResponses[0]?.Cardetail);
      if (carDetail) {
        const { PickupDate, PickupLocation } = carDetail;
        date = moment(PickupDate).format(dateFormat);
        service = 'rental car in';
        city = PickupLocation.City;
      } else {
        return ``; // Old car flow, carDetail is null
      }
    }
    if (component === 'hotel') {
      const hotel = tripSummary.HotelResponses[0];
      const { CheckInDate, CityName } = hotel;
      date = moment(CheckInDate).format(dateFormat);
      service = 'hotel in';
      city = CityName;
    }
    return `Your ${date} ${service} ${city} is out of policy`;
  };
  const VirtualPaymentComponentTypes = travelCart?.VirtualPaymentComponentTypes;
  const isVirtualPayment = VirtualPaymentComponentTypes?.length > 0;
  const getDefaultAirCardKey = airCCDetails?.filter((card) => card.value !== airnewcardValue)[0]
    ?.value;
  const getDefaultAirExtraCardKey = airExtraCCDetails?.filter(
    (card) => card.value !== airextranewcardValue
  )[0]?.value;
  const getDefaultCarCardKey = carCCDetails?.filter((card) => card.value !== carnewcardValue)[0]
    ?.value;
  const getDefaultHotelCardKey = hotelCCDetails?.filter(
    (card) => card.value !== hotelnewcardValue
  )[0]?.value;
  const getDefaultRailCardKey = railCCDetails?.filter((card) => card.value !== railnewcardValue)[0]
    ?.value;
  const params = {
    isAir,
    isHotel,
    isCar,
    isRail,
    isAirExtra,
    isAirSponsoredTravel,
    getDefaultAirCardKey,
    isAirExtraSponsoredTravel,
    getDefaultAirExtraCardKey,
    isHotelSponsoredTravel,
    getDefaultHotelCardKey,
    isCarSponsoredTravel,
    getDefaultCarCardKey,
    isRailSponsoredTravel,
    getDefaultRailCardKey,
  };
  const sponsoredCards = sponsoredTravelCreditCardsData(params);
  const isSponsoredTravel =
    (isAir && isAirSponsoredTravel) ||
    (isAirExtra && isAirExtraSponsoredTravel) ||
    (isHotel && isHotelSponsoredTravel) ||
    (isCar && isCarSponsoredTravel) ||
    (isRail && isRailSponsoredTravel);
  const shouldBtnBeDisabledForSponsoredTravel = isSponsoredTravel && termsAgreed ? false : true;
  function virtualPaymentMessageForMsite(type) {
    return showVirtualPaymentMessageForMsite(
      Platform.OS === Source.MSITE,
      VirtualPaymentComponentTypes,
      type
    );
  }

  return (
    <View style={CommonStyles.defaultContainer}>
      {paymentLoading ? (
        <ActivityIndicator size="small" />
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{
            air: {
              ...creditCardFormDetails,
              ...frontierAddressDetails,
              ccPreCreditCard: firstAirCCValidKey,
            },
            airExtra: { ...creditCardFormDetails, ccPreCreditCard: firstAirExtraCCValidKey },
            hotel: { ...creditCardFormDetails, ccPreCreditCard: firstHotelCCValidKey },
            car: { ...creditCardFormDetails, ccPreCreditCard: firstCarCCValidKey },
            rail: { ...creditCardFormDetails, ccPreCreditCard: firstRailCCValidKey },
            policy: { ...policyReasonCodes },
            selectedHotelUDIDOverNightStay,
            ReportingFields: reportingFields,
            customFields,
          }}
          validationSchema={creditCardSchema}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, formikHelpers) => {
            const {
              ccBillingAddressFrontier,
              ccCityFrontier,
              ccCountryFrontier,
              ccStateFrontier,
              ccZipCodeFrontier,
              ccBillingAddress,
              ccCity,
              ccCountry,
              ccState,
              ccZipCode,
              cardHolderEmail,
            } = values?.air;
            const address = {
              ccBillingAddressFrontier: checkbox ? ccBillingAddress : ccBillingAddressFrontier,
              ccCityFrontier: checkbox ? ccCity : ccCityFrontier,
              ccCountryFrontier: checkbox ? ccCountry : ccCountryFrontier,
              ccStateFrontier: checkbox ? ccState : ccStateFrontier,
              ccZipCodeFrontier: checkbox ? ccZipCode : ccZipCodeFrontier,
            };
            if (REBOOK_ENABLED) {
              dispatch(frontierFlightReimbursmentUpdate(address));
            }

            let airPolicyCode = '';
            values?.policy.air.forEach((elem) => {
              if (elem !== '') {
                airPolicyCode = elem;
              }
            });
            const policyExceptions = { ...values?.policy, airPolicyCode };
            objPolicyExceptions.forEach((obj) => {
              const { type, text } = obj;
              if (type?.toLowerCase() === 'tripsummary') {
                return;
              }
              policyExceptions[`${type}PolicyException`] = text?.toUpperCase();
            });
            dispatch(updateReportingFields(values, deploymentMap));
            dispatch(
              getPurchase(
                sponsoredCards,
                values,
                airCreditCard,
                airExtraCreditCard,
                hotelCreditCard,
                carCreditCard,
                railCreditCard,
                countryList,
                selectedCar && selectedCar,
                policyExceptions,
                VirtualPaymentComponentTypes,
                formikHelpers
              )
            );
          }}
        >
          {(formikProps) => (
            <SafeAreaView style={CommonStyles.flex1}>
              <Messages
                companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
                groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
                pageKey={pageKeys.PAYMENT}
              />
              <ScrollView style={CommonStyles.flex1}>
                <View style={CommonStyles.px3}>
                  <View style={CommonStyles.mt2}>
                    <CheckoutStepper
                      entities={checkoutSteps}
                      isTouchable={false}
                      flowDepth={isAir ? 2 : 1}
                      crumbsContainerStyle={CommonStyles.mt2}
                    />
                  </View>
                  {isAir && !repriceValid && (
                    <MessageAlert style={CommonStyles.mt3} message={airRepriceError} />
                  )}
                  <TotalPrice value={cartTotal} navigation={navigation} />

                  <View>
                    {policyException.map((component) => {
                      const { ComponentName, LegNumber, ComponentRequestId } = component;
                      const nameCapitalized = ComponentName?.capitalize();
                      const componentPolicy = getComponentPolicy(ComponentName, LegNumber);
                      return ComponentName.toLowerCase() !== 'tripsummary' &&
                        tripSummary[`Is${nameCapitalized}PolicyBreached`] &&
                        componentPolicy?.reasons ? (
                        <PolicyReasonsDropdown
                          key={`policy-${ComponentRequestId}`}
                          isReasonCodeDropdownAllowed={isReasonCodeDropdownAllowed}
                          redLetters={t(tKey(`${nameCapitalized}PolicyReason`))}
                          policyText={`${componentPolicy?.text?.capitalize()} ${t(
                            tKey('itinerary')
                          )}`}
                          description={getComponentDescription(ComponentName, LegNumber)}
                          label="Please select a reason why"
                          onChange={(value) => {
                            formikProps.setFieldValue(
                              getFormikValue(ComponentName, LegNumber),
                              value
                            );
                          }}
                          items={componentPolicy?.reasons}
                          value={
                            ComponentName === AIR
                              ? formikProps.values.policy[ComponentName][LegNumber - 1]
                              : formikProps.values.policy[ComponentName]
                          }
                        />
                      ) : (
                        <></>
                      );
                    })}
                  </View>

                  {hotelInclusion?.IsHotelMissingInSegment ? (
                    <PolicyReasonsDropdown
                      key="inclusion-1"
                      isReasonCodeDropdownAllowed={isReasonCodeDropdownAllowed}
                      redLetters={t(tKey('HotelPolicyReason'))}
                      policyText="OOP"
                      label="Please select a reason for why you have not added a hotel for your overnight trip:"
                      onChange={(value) => {
                        formikProps.setFieldValue('selectedHotelUDIDOverNightStay', value);
                      }}
                      items={lstHotelUDIDOverNightStays}
                      value={formikProps.values.selectedHotelUDIDOverNightStay}
                    />
                  ) : (
                    <></>
                  )}

                  {AA_ENABLED && tripSummary?.IsRedeem ? <BxAuth /> : null}
                  {isAir ? (
                    <>
                      <BillingInfoLabel text={t(tKey('FlightBilling'))} icon="flight" />
                      {isAirSponsoredTravel ? (
                        <View style={styles.sponsoredSectionView}>
                          <BaseText style={styles.sponsoredSectionTravel}>
                            No credit card or form of payment is needed - your company or sponsor is
                            paying for this flight.{' '}
                            {isVirtualPayment && virtualPaymentMessageForMsite(COMPONENT_TYPES.AIR)}
                          </BaseText>
                        </View>
                      ) : (
                        <View>
                          <ModalDropdown
                            onValueChange={(value) => {
                              formikProps.setFieldValue('air.ccPreCreditCard', +value);
                              if (isNaN(value)) {
                                setAirCreditCard(true);
                              } else {
                                setAirCreditCard(false);
                              }
                            }}
                            items={airCCDetails}
                            value={
                              REBOOK_ENABLED || airCreditCard
                                ? airnewcardValue
                                : formikProps.values.air.ccPreCreditCard
                            }
                            placeholder={{
                              label: t(tKey('SelectCard')),
                            }}
                            modalTitle={t(tKey('FlightBilling'))}
                            modalIcon="flight"
                            disabledLabel={t2('MyTripsScreen.Expired')}
                            pinneditem={{ label: t(tKey('AddNewCard')), value: airnewcardValue }}
                          />
                        </View>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {airCreditCard || REBOOK_ENABLED ? (
                    <PurchaseCreditCardFormik
                      setSelectedCountry={handleCountryChange}
                      creditCardProviders={creditCardProviders}
                      formikProps={formikProps}
                      namespace="air"
                      countryList={countryList}
                    />
                  ) : (
                    <></>
                  )}
                  {REBOOK_ENABLED ? (
                    <>
                      <BaseText style={styles.frontierHeading}>
                        Frontier flight reimbursment check address
                      </BaseText>
                      <FrontierFlightReimbursmentForm
                        setSelectedCountry={handleCountryFrontierChange}
                        isCheckboxCheckedFunc={isCheckboxChecked}
                        formikProps={formikProps}
                        countryList={countryList}
                      />
                    </>
                  ) : (
                    <></>
                  )}
                  {isAirExtra ? (
                    <>
                      <BillingInfoLabel text={t(tKey('AirExtraBilling'))} icon="flight" />
                      {isAirExtraSponsoredTravel ? (
                        <>
                          <View style={styles.sponsoredSectionView}>
                            <BaseText style={styles.sponsoredSectionTravel}>
                              No credit card or form of payment is needed - your company or sponsor
                              is paying for this flight.{' '}
                              {isVirtualPayment &&
                                virtualPaymentMessageForMsite(COMPONENT_TYPES.AIR_EXTRA)}
                            </BaseText>
                          </View>
                        </>
                      ) : (
                        <View>
                          <ModalDropdown
                            onValueChange={(value) => {
                              formikProps.setFieldValue('airExtra.ccPreCreditCard', +value);
                              if (isNaN(value)) {
                                setAirExtraCreditCard(true);
                              } else {
                                setAirExtraCreditCard(false);
                              }
                            }}
                            items={airExtraCCDetails}
                            value={formikProps.values.airExtra.ccPreCreditCard}
                            placeholder={{
                              label: t(tKey('SelectCard')),
                            }}
                            modalTitle={t(tKey('AirExtraBilling'))}
                            modalIcon="flight"
                            disabledLabel={t2('MyTripsScreen.Expired')}
                            pinneditem={{
                              label: t(tKey('AddNewCard')),
                              value: airextranewcardValue,
                            }}
                          />
                        </View>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {airExtraCreditCard && (
                    <PurchaseCreditCardFormik
                      setSelectedCountry={handleCountryChange}
                      creditCardProviders={creditCardProviders}
                      formikProps={formikProps}
                      namespace="airExtra"
                      countryList={countryList}
                    />
                  )}

                  {isHotel ? (
                    <>
                      <BillingInfoLabel text={t(tKey('HotelBilling'))} icon="hotel" />
                      {isHotelSponsoredTravel ? (
                        <>
                          <View style={styles.sponsoredSectionView}>
                            <BaseText style={styles.sponsoredSectionTravel}>
                              No credit card or form of payment is needed - your company or sponsor
                              is paying for this hotel.{' '}
                              {isVirtualPayment &&
                                virtualPaymentMessageForMsite(COMPONENT_TYPES.HOTEL)}
                            </BaseText>
                          </View>
                        </>
                      ) : (
                        <ModalDropdown
                          onValueChange={(value) => {
                            formikProps.setFieldValue('hotel.ccPreCreditCard', +value);
                            if (isNaN(value)) {
                              setHotelCreditCard(true);
                            } else {
                              setHotelCreditCard(false);
                            }
                          }}
                          items={hotelCCDetails}
                          value={
                            hotelCreditCard
                              ? hotelnewcardValue
                              : formikProps.values.hotel.ccPreCreditCard
                          }
                          placeholder={{
                            label: t(tKey('SelectCard')),
                          }}
                          modalTitle={t(tKey('HotelBilling'))}
                          modalIcon="hotel"
                          disabledLabel={t2('MyTripsScreen.Expired')}
                          pinneditem={{ label: t(tKey('AddNewCard')), value: hotelnewcardValue }}
                        />
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {hotelCreditCard && (
                    <PurchaseCreditCardFormik
                      setSelectedCountry={handleCountryChange}
                      creditCardProviders={creditCardProviders}
                      formikProps={formikProps}
                      namespace="hotel"
                      countryList={countryList}
                    />
                  )}

                  {isCar ? (
                    <>
                      <BillingInfoLabel text={t(tKey('CarBilling'))} icon="car" />
                      {isCarSponsoredTravel ? (
                        <View style={styles.sponsoredSectionView}>
                          <BaseText style={styles.sponsoredSectionTravel}>
                            No credit card or form of payment is needed - your company or sponsor is
                            paying for this car.{' '}
                            {isVirtualPayment && virtualPaymentMessageForMsite(COMPONENT_TYPES.CAR)}
                          </BaseText>
                        </View>
                      ) : (
                        <ModalDropdown
                          onValueChange={(value) => {
                            formikProps.setFieldValue('car.ccPreCreditCard', +value);
                            if (isNaN(value)) {
                              setCarCreditCard(true);
                            } else {
                              setCarCreditCard(false);
                            }
                          }}
                          items={carCCDetails}
                          value={
                            carCreditCard ? carnewcardValue : formikProps.values.car.ccPreCreditCard
                          }
                          placeholder={{
                            label: t(tKey('SelectCard')),
                          }}
                          modalTitle={t(tKey('CarBilling'))}
                          modalIcon="car"
                          disabledLabel={t2('MyTripsScreen.Expired')}
                          pinneditem={{ label: t(tKey('AddNewCard')), value: carnewcardValue }}
                        />
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {carCreditCard && (
                    <PurchaseCreditCardFormik
                      setSelectedCountry={handleCountryChange}
                      creditCardProviders={creditCardProviders}
                      formikProps={formikProps}
                      namespace="car"
                      countryList={countryList}
                    />
                  )}

                  {isRail ? (
                    <>
                      <BillingInfoLabel text={t(tKey('RailBilling'))} icon="rail" />
                      {isRailSponsoredTravel ? (
                        <View style={styles.sponsoredSectionView}>
                          <BaseText style={styles.sponsoredSectionTravel}>
                            No credit card or form of payment is needed - your company or sponsor is
                            paying for this rail.{' '}
                            {isVirtualPayment &&
                              virtualPaymentMessageForMsite(COMPONENT_TYPES.RAIL)}
                          </BaseText>
                        </View>
                      ) : (
                        <ModalDropdown
                          onValueChange={(value) => {
                            formikProps.setFieldValue('rail.ccPreCreditCard', +value);
                            if (isNaN(value)) {
                              setRailCreditCard(true);
                            } else {
                              setRailCreditCard(false);
                            }
                          }}
                          items={railCCDetails}
                          value={formikProps.values.rail.ccPreCreditCard}
                          placeholder={{
                            label: t(tKey('SelectCard')),
                          }}
                          modalTitle={t(tKey('RailBilling'))}
                          modalIcon="rail"
                          disabledLabel={t2('MyTripsScreen.Expired')}
                          pinneditem={{ label: t(tKey('AddNewCard')), value: railnewcardValue }}
                        />
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {railCreditCard && (
                    <PurchaseCreditCardFormik
                      setSelectedCountry={handleCountryChange}
                      creditCardProviders={creditCardProviders}
                      formikProps={formikProps}
                      namespace="rail"
                      countryList={countryList}
                    />
                  )}

                  <CustomFieldsFormik
                    reportingFields={reportingFields}
                    formikProps={formikProps}
                    object="customFields"
                    formikObject={formikProps?.values?.customFields}
                    formikGeneralError={formikProps.submitCount > 0}
                    formikSpecificErrorObject={formikProps.errors?.customFields}
                    deploymentMap={deploymentMap}
                  />

                  <View style={styles.termsSection}>
                    <View style={styles.termsCheckboxContainer}>
                      <Checkbox
                        selected={termsAgreed}
                        onPress={() => {
                          setTermsAgreed(!termsAgreed);
                        }}
                      />
                    </View>

                    <View style={styles.termsTextContainer}>
                      <BaseText>
                        {t(tKey('AcceptSite'))}
                        <BaseText
                          onPress={() => navigation.navigate(Screens.Terms)}
                          style={styles.linkText}
                        >
                          {t(tKey('TermsOfUser'))}
                        </BaseText>{' '}
                        <BaseText>{t(tKey('And'))}</BaseText>{' '}
                        <BaseText
                          onPress={() => navigation.navigate(Screens.Privacy)}
                          style={styles.linkText}
                        >
                          {t(tKey('PrivacyPolicy'))}
                        </BaseText>{' '}
                      </BaseText>
                    </View>
                  </View>
                  <Button
                    type={BUTTON_TYPES.TEXT}
                    style={styles.cancelButton}
                    onPress={() => {
                      REBOOK_ENABLED
                        ? navigation.navigate(Screens.RebookHome)
                        : navigation.navigate(Screens.Home);
                    }}
                    label={t(tKey('CancelTrip'))}
                  />

                  <Modal
                    animationType="fade"
                    statusBarTranslucent={false}
                    transparent={true}
                    visible={paymentInProgress}
                  >
                    <View style={styles.paymentProcess}>
                      <ActivityIndicator size="large" />
                      <BaseText>{t(tKey('PaymentProcessing'))}</BaseText>
                    </View>
                  </Modal>
                </View>
              </ScrollView>
              <FixedNextButtonBar
                buttonLabel={
                  tripSummary?.ApprovalDetail?.IsRequestApproval
                    ? 'Request Approval'
                    : t(tKey('Purchase'))
                }
                cartTotal={cartTotal}
                disabled={
                  formikProps.isSubmitting ||
                  (isSponsoredTravel
                    ? shouldBtnBeDisabledForSponsoredTravel
                    : checkFixedButtonDisable(repriceValid, formikProps, termsAgreed))
                }
                onPress={formikProps.handleSubmit}
              ></FixedNextButtonBar>
            </SafeAreaView>
          )}
        </Formik>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  paymentProcess: {
    flex: 1,
    width: '100%',
    height: 300,
    backgroundColor: Color.accent,
    ...CommonProperties.centerAlignment,
  },
  termsSection: {
    flexDirection: 'row',
    ...CommonProperties.p3,
    justifyContent: 'space-between',
  },
  termsCheckboxContainer: {
    width: '10%',
    ...CommonProperties.mx2,
  },
  termsTextContainer: {
    width: '90%',
    ...CommonProperties.mx2,
  },
  linkText: {
    color: Color.primary,
  },
  policyText: {
    fontWeight: 'bold',
    color: Color.required,
    textAlign: 'left',
  },
  policyReason: {
    textAlign: 'left',
  },
  policyException: {
    fontWeight: 'bold',
    color: Color.black,
  },
  frontierHeading: {
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 6,
    fontSize: 14,
  },
  sponsoredSectionView: {
    marginTop: 8,
    marginBottom: 8,
    textAlign: 'left',
    backgroundColor: 'rgb(212 212 212)',
    borderWidth: 1,
    borderColor: '#c7c7d0',
    padding: 5,
    borderRadius: 6,
    fontWeight: 'bold',
  },
  sponsoredSectionTravel: {
    marginLeft: 29,
  },
  cancelButton: {
    alignSelf: 'center',
  },
});

export default PurchaseScreen;
